/**
 * DenominacionesInput - Conteo de efectivo por denominación USD
 * Muestra el catálogo (monedas 1¢ a $1 y billetes $1 a $100) con
 * campos de cantidad, subtotales y total calculado automáticamente.
 */
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import { formatCurrency } from '../../utils/formatters';

/**
 * Clave única de una denominación del catálogo
 */
export const denomKey = (d) => `${d.tipo}-${Number(d.denominacion).toFixed(2)}`;

/**
 * Calcula el total (en centavos) de un mapa de cantidades sobre el catálogo
 */
export const calcularTotalCentavos = (catalogo, cantidades) => {
  return catalogo.reduce((sum, d) => {
    const qty = parseInt(cantidades[denomKey(d)], 10) || 0;
    return sum + Math.round(d.denominacion * 100) * qty;
  }, 0);
};

/**
 * Convierte el mapa de cantidades al arreglo que espera el backend
 */
export const construirDetalle = (catalogo, cantidades) => {
  return catalogo
    .map((d) => ({
      tipo: d.tipo,
      denominacion: d.denominacion,
      cantidad: parseInt(cantidades[denomKey(d)], 10) || 0,
    }))
    .filter((d) => d.cantidad > 0);
};

const DenominacionesInput = ({ catalogo, cantidades, onChange, disponibles = null }) => {
  const handleCantidad = (key, valor) => {
    const cantidad = valor === '' ? '' : Math.max(0, parseInt(valor, 10) || 0);
    onChange({ ...cantidades, [key]: cantidad });
  };

  const totalCentavos = calcularTotalCentavos(catalogo, cantidades);

  const monedas = catalogo.filter((d) => d.tipo === 'moneda');
  const billetes = catalogo.filter((d) => d.tipo === 'billete');

  const renderGrupo = (titulo, items) => (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>{titulo}</TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold', width: 130 }}>
              Cantidad
            </TableCell>
            {disponibles && (
              <TableCell align="center" sx={{ fontWeight: 'bold', width: 100 }}>
                Disponibles
              </TableCell>
            )}
            <TableCell align="right" sx={{ fontWeight: 'bold', width: 120 }}>
              Subtotal
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((d) => {
            const key = denomKey(d);
            const qty = parseInt(cantidades[key], 10) || 0;
            const disponible = disponibles
              ? disponibles.find(
                  (x) =>
                    x.tipo === d.tipo &&
                    Math.round(x.denominacion * 100) === Math.round(d.denominacion * 100)
                )?.cantidad ?? 0
              : null;
            return (
              <TableRow key={key} hover>
                <TableCell>{d.etiqueta}</TableCell>
                <TableCell align="center">
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }}
                    value={cantidades[key] ?? ''}
                    onChange={(e) => handleCantidad(key, e.target.value)}
                    sx={{ width: 100 }}
                  />
                </TableCell>
                {disponibles && (
                  <TableCell align="center">
                    <Chip
                      label={disponible}
                      size="small"
                      color={qty > disponible ? 'error' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                )}
                <TableCell align="right">
                  {qty > 0 ? formatCurrency((Math.round(d.denominacion * 100) * qty) / 100) : '-'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      {renderGrupo('Monedas', monedas)}
      {renderGrupo('Billetes', billetes)}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2">Total contado:</Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {formatCurrency(totalCentavos / 100)}
        </Typography>
      </Box>
    </Box>
  );
};

DenominacionesInput.propTypes = {
  catalogo: PropTypes.array.isRequired,
  cantidades: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  disponibles: PropTypes.array,
};

export default DenominacionesInput;
